import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { PageHeader, type BreadcrumbItem } from './PageHeader';

export interface PageShellProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  breadcrumb?: ReadonlyArray<BreadcrumbItem>;
  actions?: ReactNode;
  /** Optional back link rendered above the title. */
  backHref?: string;
  backLabel?: string;
  /** Sticky page header — keeps title + actions pinned while scrolling. */
  sticky?: boolean;
  /**
   * Dense baseline title — pass through to PageHeader. Default true.
   */
  dense?: boolean;
  /** Gap between header and content sections. Default 16px (compact). */
  gap?: 'compact' | 'comfortable';
  children: ReactNode;
}

export const PageShell = ({
  title,
  description,
  breadcrumb,
  actions,
  backHref,
  backLabel,
  sticky = false,
  dense = true,
  gap = 'compact',
  children,
  className,
  ...rest
}: PageShellProps) => (
  <div className={cn('flex flex-col', gap === 'compact' ? 'gap-4' : 'gap-6', className)} {...rest}>
    <PageHeader
      title={title}
      dense={dense}
      sticky={sticky}
      {...(description ? { description } : {})}
      {...(breadcrumb ? { breadcrumb } : {})}
      {...(actions ? { actions } : {})}
      {...(backHref ? { backHref } : {})}
      {...(backLabel ? { backLabel } : {})}
    />
    {children}
  </div>
);
